import moment from "moment";
import { Button } from "@rio-cloud/rio-uikit";
import DateRangePicker from "../DateRangePicker";
import { useIntl } from "react-intl";
import { useT } from "@hooks";

interface MonthNavigationProps {
  currentMonth: string;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  onClick?: () => void;
  showDateRangePicker?: boolean;
  onCloseDateRangePicker?: () => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
}

const resolveMomentLocale = (currentLocale: string) => {
  if (currentLocale.startsWith("en")) return "en-gb";
  if (currentLocale.startsWith("es")) return "es";
  return "pt-br";
};

export default function MonthNavigation({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onClick,
  showDateRangePicker,
  onCloseDateRangePicker,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: Readonly<MonthNavigationProps>) {
  const { locale } = useIntl();
  const t = useT();

  const momentLocale = resolveMomentLocale(locale);
  moment.locale(momentLocale);

  const getDisplayText = () => {
    if (startDate && endDate) {
      const start = moment(startDate, "YYYY-MM-DD");
      const end = moment(endDate, "YYYY-MM-DD");

      const isStartOfMonth = start.date() === 1;
      const isEndOfMonth = end.date() === end.daysInMonth();
      const isSameMonth = start.month() === end.month() && start.year() === end.year();

      if (isStartOfMonth && isEndOfMonth && isSameMonth) {
        const monthName = start.format("MMMM").toUpperCase();
        return `${monthName} ${start.format("YYYY")}`;
      }

      const formattedStart = start.format("DD/MM/YYYY");
      const formattedEnd = end.format("DD/MM/YYYY");
      return t("period.range").replace("{start}", formattedStart).replace("{end}", formattedEnd);
    }
    return currentMonth;
  };

  return (
    <div className="position-relative">
      <div className="display-flex align-items-center justify-content-between gap-20 bg-lightest padding-15 rounded width-100pct">
        <Button
          variant={Button.VARIANT_LINK}
          iconOnly
          iconName="rioglyph-triangle-left"
          onClick={(e) => {
            e?.stopPropagation();
            onPrevMonth?.();
          }}
        />
        <button
          type="button"
          className={`month-navigation-label flex-1-1 text-size-h4 text-bold text-color-primary ${
            onClick ? "cursor-pointer" : "cursor-default"
          }`}
          onClick={onClick}
        >
          {getDisplayText()}
        </button>
        <Button
          iconOnly
          variant={Button.VARIANT_LINK}
          iconName="rioglyph-triangle-right"
          onClick={(e) => {
            e?.stopPropagation();
            onNextMonth?.();
          }}
        />
      </div>

      {showDateRangePicker && (
        <div className="position-absolute left-0 right-0 margin-left-auto margin-right-auto margin-top-4 z-index-10 width-100pct width-50pct-md">
          <DateRangePicker
            endDate={endDate}
            startDate={startDate}
            show={showDateRangePicker}
            onEndDateChange={onEndDateChange}
            onStartDateChange={onStartDateChange}
            onClose={onCloseDateRangePicker || (() => {})}
          />
        </div>
      )}
    </div>
  );
}
